// pattern: Imperative Shell
// localStorage wrapper for student quiz attempts

import type { QuizAttempt } from '../core/question-model.ts';
import type { FlashcardProgress } from '../core/progress-tracker.ts';

const STORAGE_KEY = 'keyquiz_attempts';

function flashcardProgressKey(quizId: string): string {
  return `keyquiz_flashcard_progress_${quizId}`;
}

export type StorageResult<T> = {
  readonly success: boolean;
  readonly data?: T;
  readonly error?: string;
};

/** Save a new quiz attempt (appends to existing list) */
export function saveAttempt(attempt: QuizAttempt): StorageResult<void> {
  try {
    const existing = loadAttempts();
    const attempts = [...(existing.success && existing.data ? existing.data : []), attempt];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'failed to save attempt',
    };
  }
}

/** Load all attempts for a specific quiz */
export function loadAttemptsForQuiz(quizId: string): StorageResult<Array<QuizAttempt>> {
  const all = loadAttempts();
  if (!all.success) return all;
  return {
    success: true,
    data: (all.data ?? []).filter((a) => a.quizId === quizId),
  };
}

/** Load all attempts from localStorage */
export function loadAttempts(): StorageResult<Array<QuizAttempt>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { success: true, data: [] };
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return { success: true, data: [] };
    // trusted: localStorage is same-origin only
    return { success: true, data: parsed as Array<QuizAttempt> };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'failed to load attempts',
    };
  }
}

/** Check if localStorage is available */
export function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/** Save flashcard progress for a quiz (overwrites all card progress for that quiz) */
export function saveFlashcardProgress(
  quizId: string,
  progress: ReadonlyArray<FlashcardProgress>
): StorageResult<void> {
  try {
    localStorage.setItem(flashcardProgressKey(quizId), JSON.stringify(progress));
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'failed to save flashcard progress',
    };
  }
}

/** Load flashcard progress for a quiz */
export function loadFlashcardProgress(quizId: string): StorageResult<Array<FlashcardProgress>> {
  try {
    const raw = localStorage.getItem(flashcardProgressKey(quizId));
    if (!raw) return { success: true, data: [] };
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return { success: true, data: [] };
    // trusted: localStorage is same-origin only
    return { success: true, data: parsed as Array<FlashcardProgress> };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'failed to load flashcard progress',
    };
  }
}

/** Clear flashcard progress for a quiz */
export function clearFlashcardProgress(quizId: string): StorageResult<void> {
  try {
    localStorage.removeItem(flashcardProgressKey(quizId));
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'failed to clear flashcard progress',
    };
  }
}
